import React, { useState, useEffect } from 'react';
import { CheckSquare, Clock, AlertCircle, Filter } from 'lucide-react';
import Navigation from '../components/Navigation';
import apiClient from '../utils/api';
import { handleApiError } from '../utils/errorHandler';
import { useToast } from '../hooks/useToast';
import { useTranslation } from '../i18n';
import { format } from 'date-fns';

const Tasks = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePhase, setActivePhase] = useState('next_action');

  const toast = useToast();

  useEffect(() => {
    fetchTasks();
  }, [activePhase]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('status', 'active');
      if (activePhase) params.append('category', activePhase);
      
      const response = await apiClient.get(`/tasks?${params}`);
      setTasks(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load tasks', t));
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'completed' : 'active';
      await apiClient.patch(`/tasks/${taskId}`, { status: newStatus });
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task.task_id === taskId ? { ...task, status: newStatus } : task
      ));
      
      toast.showSuccess(`Task marked as ${newStatus}`);
    } catch (err) {
      const errorMessage = handleApiError(err, 'Failed to update task status', t);
      setError(errorMessage);
      toast.showError(errorMessage);
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: 'badge-secondary',
      medium: 'badge-primary',
      high: 'badge-warning',
      urgent: 'badge-danger'
    };
    return badges[priority] || 'badge-secondary';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'next_action':
        return <CheckSquare size={16} />;
      case 'scheduled':
        return <Clock size={16} />;
      case 'waiting_for':
        return <AlertCircle size={16} />;
      default:
        return <CheckSquare size={16} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container safe-area-bottom">
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="text-lg" style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
            {t('tasks')}
          </h1>
          <p className="text-sm" style={{ color: '#666' }}>
            {t('manageTasks')}
          </p>
        </div>

        {error && (
          <div className="error">{error}</div>
        )}

        {/* GTD Phases Navigation */}
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
          <style>{`
            @media (min-width: 768px) {
              .gtd-buttons {
                grid-template-columns: repeat(4, 1fr) !important;
              }
            }
          `}</style>
          <div className="gtd-buttons" style={{ display: 'contents' }}>
            <button
              onClick={() => setActivePhase('next_action')}
              className={`btn ${activePhase === 'next_action' ? 'btn-primary' : 'btn-outline'}`}
              style={{ 
                fontSize: '0.875rem',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('nextActions')}
            </button>
            <button
              onClick={() => setActivePhase('scheduled')}
              className={`btn ${activePhase === 'scheduled' ? 'btn-primary' : 'btn-outline'}`}
              style={{ 
                fontSize: '0.875rem',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('scheduled')}
            </button>
            <button
              onClick={() => setActivePhase('waiting_for')}
              className={`btn ${activePhase === 'waiting_for' ? 'btn-primary' : 'btn-outline'}`}
              style={{ 
                fontSize: '0.875rem',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('waitingFor')}
            </button>
            <button
              onClick={() => setActivePhase('someday_maybe')}
              className={`btn ${activePhase === 'someday_maybe' ? 'btn-primary' : 'btn-outline'}`}
              style={{ 
                fontSize: '0.875rem',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('someday')}
            </button>
          </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">
              {getCategoryIcon(activePhase)}
              {activePhase === 'next_action' && t('nextActionsTasks')}
              {activePhase === 'scheduled' && t('scheduledTasks')}
              {activePhase === 'waiting_for' && t('waitingForTasks')}
              {activePhase === 'someday_maybe' && t('somedayMaybeTasks')}
              <span style={{ color: '#666', fontWeight: 'normal' }}>({tasks.length})</span>
            </h2>
          </div>
          
          {tasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <CheckSquare size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>{t('noTasks')}</p>
              <p style={{ fontSize: '0.875rem' }}>
                {t('startAddingTasks')}
              </p>
            </div>
          ) : (
            <ul className="list">
              {(Array.isArray(tasks) ? tasks : []).map((task) => (
                <li key={task.task_id} className="list-item">
                  <div className="list-item-content">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => toggleTaskStatus(task.task_id, task.status)}
                        style={{
                          background: 'none',
                          border: `2px solid ${task.status === 'completed' ? '#28a745' : '#ddd'}`,
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          color: task.status === 'completed' ? '#28a745' : 'transparent',
                          flexShrink: 0
                        }}
                      >
                        ✓
                      </button>
                      <div className="list-item-title" style={{ 
                        textDecoration: task.status === 'completed' ? 'line-through' : 'none', 
                        opacity: task.status === 'completed' ? 0.6 : 1,
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word'
                      }}>
                        {task.title}
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0, flexWrap: 'wrap' }}>
                        <span className={`badge ${getPriorityBadge(task.priority)}`}>
                          {task.priority}
                        </span>
                        {task.platform && (
                          <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                            {task.platform === 'telegram' ? '📱' : task.platform === 'whatsapp' ? '💬' : task.platform}
                          </span>
                        )}
                        {task.integration_name === 'google_tasks' && (
                          <span className="badge" style={{ 
                            fontSize: '0.7rem', 
                            backgroundColor: '#e8f5e9', 
                            color: '#2e7d32',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }} title="Synced with Google Tasks">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Google
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {task.description && (
                      <div style={{ 
                        color: '#666', 
                        fontSize: '0.875rem', 
                        marginBottom: '0.5rem',
                        wordBreak: 'break-word',
                        lineHeight: '1.4'
                      }}>
                        {task.description}
                      </div>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '0.75rem', 
                      fontSize: '0.75rem', 
                      color: '#666',
                      marginBottom: '0.5rem'
                    }}>
                      {task.due_date && (
                        <span style={{ whiteSpace: 'nowrap' }}>{t('due')}: {formatDate(task.due_date)}</span>
                      )}
                      {task.waiting_for && (
                        <span style={{ wordBreak: 'break-word' }}>{t('waitingFor')}: {task.waiting_for}</span>
                      )}
                    </div>
                    
                    {task.tags && task.tags.length > 0 && (
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {task.tags.map((tag, index) => (
                          <span
                            key={index}
                            style={{
                              display: 'inline-block',
                              backgroundColor: '#f0f0f0',
                              color: '#666',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              wordBreak: 'break-word'
                            }}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-end', 
                    gap: '0.25rem',
                    flexShrink: 0,
                    minWidth: 'fit-content'
                  }}>
                    <span className={`badge ${task.status === 'completed' ? 'badge-success' : 'badge-primary'}`}>
                      {task.status}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#999', whiteSpace: 'nowrap' }}>
                      {formatDate(task.updated_at)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default Tasks;